import { endOfWeek, format, startOfWeek } from 'date-fns';
import type { OpenClockSession, TimesheetPeriodRow } from '@/types/timesheet';
import { dateOf } from './entry-time';

// Pay periods are Monday–Sunday weeks; the backend approval rows are keyed on
// exactly these bounds, so every screen has to agree on them.
export const WEEK_STARTS_ON = 1 as const;

/** How an approval instant is shown — "Sep 15, 4:02 PM". */
export const APPROVED_AT_FORMAT: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
};

export interface PayPeriod {
  start: Date;
  end: Date;
  startKey: string;
  endKey: string;
}

export function payPeriodFor(date: Date): PayPeriod {
  const start = startOfWeek(date, { weekStartsOn: WEEK_STARTS_ON });
  const end = endOfWeek(date, { weekStartsOn: WEEK_STARTS_ON });
  return {
    start,
    end,
    startKey: format(start, 'yyyy-MM-dd'),
    endKey: format(end, 'yyyy-MM-dd'),
  };
}

/** "Sep 8 – 14, 2026", "Aug 31 – Sep 6, 2026", "Dec 29, 2025 – Jan 4, 2026". */
export function formatPeriodLabel(start: Date, end: Date): string {
  if (start.getFullYear() !== end.getFullYear()) {
    return `${format(start, 'MMM d, yyyy')} – ${format(end, 'MMM d, yyyy')}`;
  }
  if (start.getMonth() !== end.getMonth()) {
    return `${format(start, 'MMM d')} – ${format(end, 'MMM d, yyyy')}`;
  }
  return `${format(start, 'MMM d')} – ${format(end, 'd, yyyy')}`;
}

/**
 * A `?week=` value that names a real calendar day, or undefined.
 *
 * The param is shareable page state, so it arrives hand-edited or truncated.
 * Matching the shape is not enough: "2026-99-99" makes an Invalid Date that
 * throws the moment it is formatted, and "2026-02-31" quietly rolls into
 * March. Only a value that survives a round trip through a Date is kept.
 */
export function parseWeekParam(value: unknown): string | undefined {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return undefined;
  return format(date, 'yyyy-MM-dd') === value ? value : undefined;
}

/** A yyyy-MM-dd key as a local-midnight Date, for display only. */
export function dayDate(dateKey: string): Date {
  return new Date(`${dateKey}T00:00:00`);
}

/** Payroll hours: at most two decimals, no trailing zeros — "8h", "7.5h", "7.25h". */
export function formatHours(hours: number): string {
  return `${Number(hours.toFixed(2))}h`;
}

/** A single shift's length — "3h 12m", "45m", "8h". */
export function formatDuration(totalMinutes: number): string {
  const minutes = Math.max(0, Math.round(totalMinutes));
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

export function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || '?'
  );
}

export type TimesheetStatusFilter = 'all' | 'pending' | 'approved' | 'overtime';
export type TimesheetSortKey = 'name' | 'total' | 'overtime' | 'status';
export type SortDirection = 'asc' | 'desc';

export function matchesStatusFilter(row: TimesheetPeriodRow, filter: TimesheetStatusFilter) {
  switch (filter) {
    case 'pending':
      return row.approval.status === 'OPEN';
    case 'approved':
      return row.approval.status === 'APPROVED';
    case 'overtime':
      return row.totals.overtime > 0;
    default:
      return true;
  }
}

export function countByStatusFilter(rows: TimesheetPeriodRow[]) {
  return {
    all: rows.length,
    pending: rows.filter((row) => matchesStatusFilter(row, 'pending')).length,
    approved: rows.filter((row) => matchesStatusFilter(row, 'approved')).length,
    overtime: rows.filter((row) => matchesStatusFilter(row, 'overtime')).length,
  } satisfies Record<TimesheetStatusFilter, number>;
}

export function filterAndSortRows(
  rows: TimesheetPeriodRow[],
  {
    query,
    status,
    sortKey,
    sortDir,
  }: {
    query: string;
    status: TimesheetStatusFilter;
    sortKey: TimesheetSortKey;
    sortDir: SortDirection;
  },
): TimesheetPeriodRow[] {
  const needle = query.trim().toLowerCase();
  const filtered = rows.filter(
    (row) =>
      matchesStatusFilter(row, status) &&
      (!needle || row.technicianName.toLowerCase().includes(needle)),
  );
  const byName = (a: TimesheetPeriodRow, b: TimesheetPeriodRow) =>
    a.technicianName.localeCompare(b.technicianName);
  const compare = (a: TimesheetPeriodRow, b: TimesheetPeriodRow) => {
    switch (sortKey) {
      case 'total':
        return a.totals.total - b.totals.total || byName(a, b);
      case 'overtime':
        return a.totals.overtime - b.totals.overtime || byName(a, b);
      case 'status':
        // Pending sorts before approved: it is the work still to do.
        return (
          Number(a.approval.status === 'APPROVED') - Number(b.approval.status === 'APPROVED') ||
          byName(a, b)
        );
      default:
        return byName(a, b);
    }
  };
  return [...filtered].sort((a, b) => (sortDir === 'asc' ? compare(a, b) : -compare(a, b)));
}

/** Per-day team totals across `rows`, index-aligned with each row's `days`. */
export function teamDayTotals(rows: TimesheetPeriodRow[]): number[] {
  const length = rows[0]?.days.length ?? 0;
  const totals = Array.from({ length }, () => 0);
  for (const row of rows) {
    row.days.forEach((day, index) => {
      totals[index] += day.totalHours;
    });
  }
  return totals;
}

export function sumTotals(rows: TimesheetPeriodRow[]) {
  return rows.reduce(
    (acc, row) => ({
      regular: acc.regular + row.totals.regular,
      overtime: acc.overtime + row.totals.overtime,
      travel: acc.travel + row.totals.travel,
      total: acc.total + row.totals.total,
    }),
    { regular: 0, overtime: 0, travel: 0, total: 0 },
  );
}

/** One open session per technician (the backend allows one); the oldest wins. */
export function openSessionsByTechnician(sessions: OpenClockSession[] | undefined) {
  const map = new Map<string, OpenClockSession>();
  for (const session of sessions ?? []) {
    const existing = map.get(session.technicianId);
    if (!existing || session.clockIn < existing.clockIn) map.set(session.technicianId, session);
  }
  return map;
}

/**
 * The open session that blocks approving this period, if any. The backend
 * refuses to approve a period holding an entry with no clock-out, bucketed by
 * the technician's local day — mirror that here so the button can say why
 * instead of failing.
 */
export function blockingOpenSession(
  session: OpenClockSession | undefined,
  period: { startKey: string; endKey: string },
  tz?: string,
): OpenClockSession | null {
  if (!session) return null;
  const key = dateOf(session.clockIn, tz);
  return key >= period.startKey && key <= period.endKey ? session : null;
}

/**
 * Worked minutes for a wall-clock start/end pair, net of break — the live
 * preview under a time form. An end before the start is an overnight shift,
 * matching how `resolveClockOut` saves it. Null until both times are set.
 */
export function shiftPreview(
  start: string,
  end: string,
  breakMinutes: number,
): { minutes: number; overnight: boolean } | null {
  const toMinutes = (value: string) => {
    const [h, m] = value.split(':').map(Number);
    return Number.isNaN(h) || Number.isNaN(m) ? null : h * 60 + m;
  };
  if (!start || !end) return null;
  const from = toMinutes(start);
  const to = toMinutes(end);
  if (from == null || to == null) return null;
  const overnight = to < from;
  const span = (overnight ? to + 24 * 60 : to) - from;
  return {
    minutes: Math.max(0, span - (Number.isFinite(breakMinutes) ? breakMinutes : 0)),
    overnight,
  };
}

// ─── Time-of-day picking ──────────────────────────────────────────────────

/** Every quarter hour of a day as HH:mm, for the time picker's list. */
export const QUARTER_HOUR_SLOTS: string[] = Array.from({ length: 96 }, (_, i) => {
  const minutes = i * 15;
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
});

/** HH:mm → "8:30 AM". */
export function formatTimeOfDay(value: string): string {
  const [h, m] = value.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return '';
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${suffix}`;
}

/**
 * Whatever someone types into a time box → HH:mm, or null. Accepts "8",
 * "830", "8:30", "8.30", "8:30pm", "8p", "16:15" and "1615". A bare hour with
 * no am/pm is read on the 24-hour clock, so "8" is morning and "16" is 4 PM.
 */
export function parseTimeOfDay(input: string): string | null {
  const text = input.trim().toLowerCase().replace(/\s+/g, '');
  const match = text.match(/^(\d{1,2})(?:[:.]?(\d{2}))?(a|am|p|pm)?$/);
  if (!match) return null;
  let hour = Number(match[1]);
  const minute = match[2] ? Number(match[2]) : 0;
  const meridiem = match[3];
  if (minute > 59) return null;
  if (meridiem) {
    if (hour < 1 || hour > 12) return null;
    if (meridiem.startsWith('p') && hour !== 12) hour += 12;
    if (meridiem.startsWith('a') && hour === 12) hour = 0;
  } else if (hour > 23) {
    return null;
  }
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/** Minutes from `from` to `to` (HH:mm), wrapping past midnight like a shift does. */
export function minutesBetween(from: string, to: string): number | null {
  const toMinutes = (value: string) => {
    const [h, m] = value.split(':').map(Number);
    return Number.isNaN(h) || Number.isNaN(m) ? null : h * 60 + m;
  };
  const a = toMinutes(from);
  const b = toMinutes(to);
  if (a == null || b == null) return null;
  return b >= a ? b - a : b + 24 * 60 - a;
}
